import React, { useState, useEffect } from "react";
import { Input, Switch, Button, Space, message, Divider, Card, Tag, Checkbox, Typography, Tooltip, Row, Col } from "antd";
import { ContrastTag } from "../../../../../components/shared";
import { PlusOutlined, DeleteOutlined, DragOutlined, SaveOutlined, InfoCircleOutlined, DatabaseOutlined } from "@ant-design/icons";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { fetchWorkflowTemplate, createWorkflowTemplate, updateWorkflowTemplate } from "../../../../../api/healthcare/Consultation/ConsultApi";
import { WorkflowTemplate, WorkflowStep } from "../../../../../types/appointments/ConsultWorkflowTypes";
import { WORKFLOW_DATA_SOURCES } from "../../../../../constants/dataSources";
import DetailDrawer from "../../../../../components/shared/DetailDrawer";
import StepEditorDrawer from "./StepEditorDrawer";

const { TextArea } = Input;
const { Text } = Typography;

interface WorkflowBuilderDrawerProps {
	visible: boolean;
	workflow: WorkflowTemplate | null;
	onClose: (shouldRefresh?: boolean) => void;
}

interface SortableStepProps {
	step: WorkflowStep;
	index: number;
	onEdit: (index: number) => void;
	onDelete: (index: number) => void;
}

// Step Card
const SortableStep: React.FC<SortableStepProps> = ({ step, index, onEdit, onDelete }) => {
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: step.step_order });

	return (
		<div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, marginBottom: 6 }}>
			<Card
				size="small"
				style={{ cursor: "move" }}
				styles={{ body: { padding: "8px 12px" } }}
			>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
					<div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
						<div {...attributes} {...listeners} style={{ cursor: "grab", color: "var(--mg-text-secondary)" }}>
							<DragOutlined style={{ fontSize: 12 }} />
						</div>
						<div style={{ flex: 1 }}>
							<Text strong style={{ fontSize: 12 }}>{step.title}</Text>
							{step.description && (
								<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 2 }}>
									{step.description.substring(0, 60)}…
								</Text>
							)}
						</div>
					</div>
					<Space size={4}>
						<Button type="link" size="small" onClick={() => onEdit(index)} style={{ fontSize: 11 }}>Edit</Button>
						<Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(index)} />
					</Space>
				</div>
			</Card>
		</div>
	);
};

const WorkflowBuilderDrawer: React.FC<WorkflowBuilderDrawerProps> = ({ visible, workflow, onClose }) => {
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [templateData, setTemplateData] = useState<WorkflowTemplate>({
		id: 0,
		name: "",
		description: "",
		version: 1,
		is_active: true,
		company: "",
		category: "",
		steps: [],
	});
	const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null);
	const [stepFormVisible, setStepFormVisible] = useState(false);
	const [selectedDataSources, setSelectedDataSources] = useState<string[]>([]);
	const availableDataSources = WORKFLOW_DATA_SOURCES;

	const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

	useEffect(() => {
		if (visible) {
			if (workflow) {
				loadWorkflowDetails(workflow.id);
			} else {
				resetForm();
			}
		}
	}, [visible, workflow]);

	const loadWorkflowDetails = async (workflowId: number) => {
		setLoading(true);
		try {
			const response = await fetchWorkflowTemplate(workflowId);
			const workflowData = response.template || response;

			if (workflowData.data_sources && workflowData.data_sources.length > 0) {
				const sources = workflowData.data_sources.map((ds: any) => ds.source_identifier);
				setSelectedDataSources(sources);
			}

			setTemplateData(workflowData);
		} catch (error) {
			console.error("Failed to load workflow details:", error);
			message.error("Failed to load workflow details");
		} finally {
			setLoading(false);
		}
	};

	const resetForm = () => {
		setTemplateData({
			id: 0,
			name: "",
			description: "",
			version: 1,
			is_active: true,
			company: "",
			category: "",
			steps: [],
		});
		setEditingStepIndex(null);
		setStepFormVisible(false);
		setSelectedDataSources([]);
	};

	const updateTemplateField = <K extends keyof WorkflowTemplate>(field: K, value: WorkflowTemplate[K]) => {
		setTemplateData((prev) => ({ ...prev, [field]: value }));
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (over && active.id !== over.id) {
			setTemplateData((prev) => {
				const steps = prev.steps || [];
				const oldIndex = steps.findIndex((item) => item.step_order === active.id);
				const newIndex = steps.findIndex((item) => item.step_order === over.id);
				const reordered = arrayMove(steps, oldIndex, newIndex);
				const reorderedWithUpdatedOrder = reordered.map((item, index) => ({ ...item, step_order: index + 1 }));
				return { ...prev, steps: reorderedWithUpdatedOrder };
			});
		}
	};

	const handleAddStep = () => {
		setEditingStepIndex(null);
		setStepFormVisible(true);
	};

	const handleEditStep = (index: number) => {
		setEditingStepIndex(index);
		setStepFormVisible(true);
	};

	const handleDeleteStep = (index: number) => {
		setTemplateData((prev) => ({
			...prev,
			steps: (prev.steps || []).filter((_, i) => i !== index).map((item, i) => ({ ...item, step_order: i + 1 })),
		}));
		message.success("Step removed");
	};

	const handleStepFormSubmit = (values: WorkflowStep) => {
		if (editingStepIndex !== null) {
			setTemplateData((prev) => ({
				...prev,
				steps: (prev.steps || []).map((step, i) => (i === editingStepIndex ? { ...values, step_order: step.step_order } : step)),
			}));
			message.success("Step updated");
		} else {
			const newStep: WorkflowStep = { ...values, step_order: (templateData.steps?.length || 0) + 1 };
			setTemplateData((prev) => ({
				...prev,
				steps: [...(prev.steps || []), newStep],
			}));
			message.success("Step added");
		}
		setStepFormVisible(false);
		setEditingStepIndex(null);
	};

	const handleSave = async () => {
		try {
			if (!templateData.name) {
				message.error("Workflow name is required");
				return;
			}

			setSaving(true);

			// Prepare data with data sources
			const saveData = {
				...templateData,
				data_sources: selectedDataSources.map((identifier) => {
					const source = availableDataSources.find((s) => s.identifier === identifier);
					return {
						source_type: source?.source_type || "custom",
						source_identifier: identifier,
						is_required: source?.is_required || false,
					};
				}),
			};

			if (workflow?.id) {
				await updateWorkflowTemplate(workflow.id, saveData);
				message.success("Workflow updated");
			} else {
				await createWorkflowTemplate(saveData);
				message.success("Workflow created");
			}
			onClose(true);
		} catch (error) {
			message.error("Failed to save workflow");
		} finally {
			setSaving(false);
		}
	};

	return (
		<DetailDrawer
			open={visible}
			onClose={() => onClose(false)}
			width={680}
			title={workflow ? "Edit Workflow" : "Create Workflow"}
			subtitle={workflow ? `v${templateData.version}` : undefined}
			loading={loading}
			loadingMessage="Loading workflow…"
			headerActions={
				<Space>
					<Button onClick={() => onClose(false)}>Cancel</Button>
					<Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>Save</Button>
				</Space>
			}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
				{/* Workflow Name */}
				<div>
					<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
						Workflow Name <span style={{ color: "var(--mg-danger)" }}>*</span>
					</Text>
					<Input placeholder="e.g., Genetic Consultation" value={templateData.name} onChange={(e) => updateTemplateField("name", e.target.value)} />
				</div>

				{/* Description */}
				<div>
					<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Description</Text>
					<TextArea rows={2} placeholder="Brief description" value={templateData.description} onChange={(e) => updateTemplateField("description", e.target.value)} />
				</div>

				{/* Company */}
				<div>
					<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Company</Text>
					<Input placeholder="Company name" value={templateData.company} onChange={(e) => updateTemplateField("company", e.target.value)} />
				</div>

				{/* Category */}
				<div>
					<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Category</Text>
					<Input placeholder="e.g., Genetic Counseling" value={templateData.category} onChange={(e) => updateTemplateField("category", e.target.value)} />
				</div>

				{/* Active Switch */}
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<Text style={{ fontSize: 13 }}>Active</Text>
					<Switch size="small" checked={templateData.is_active} onChange={(checked) => updateTemplateField("is_active", checked)} />
				</div>

				<Divider style={{ margin: "4px 0" }} />

				{/* Steps Section */}
				<div>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
						<Text strong style={{ fontSize: 13 }}>
							Steps ({templateData.steps?.length || 0})
						</Text>
						<Button type="dashed" icon={<PlusOutlined />} onClick={handleAddStep} size="small">Add Step</Button>
					</div>

					{!templateData.steps || templateData.steps.length === 0 ? (
						<div style={{ textAlign: "center", padding: "16px 0", fontSize: 12, color: "var(--mg-text-disabled)" }}>
							No steps yet. Click "Add Step" to begin.
						</div>
					) : (
						<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
							<SortableContext items={templateData.steps.map((s) => s.step_order)} strategy={verticalListSortingStrategy}>
								{templateData.steps.map((step, index) => (
									<SortableStep key={step.step_order} step={step} index={index} onEdit={handleEditStep} onDelete={handleDeleteStep} />
								))}
							</SortableContext>
						</DndContext>
					)}
				</div>

				<Divider style={{ margin: "4px 0" }} />

				{/* Data Sources */}
				<div>
					<div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
						<DatabaseOutlined style={{ fontSize: 13, color: "var(--mg-primary)" }} />
						<Text strong style={{ fontSize: 13 }}>Data Sources</Text>
						<Tooltip title="Selected sources are available to all steps as template variables">
							<InfoCircleOutlined style={{ fontSize: 11, color: "var(--mg-text-secondary)" }} />
						</Tooltip>
					</div>
					<Checkbox.Group value={selectedDataSources} onChange={(v) => setSelectedDataSources(v as string[])} style={{ width: "100%" }}>
						<Row gutter={[8, 8]}>
							{availableDataSources.map((source) => (
								<Col span={12} key={source.identifier}>
									<Card
										size="small"
										style={{ height: "100%", border: "1px solid var(--mg-border)" }}
										styles={{ body: { padding: "8px 10px" } }}
									>
										<Checkbox value={source.identifier}>
											<div>
												<Text strong style={{ fontSize: 12 }}>{source.name}</Text>
												<div style={{ marginTop: 3 }}>
													<ContrastTag background="var(--mg-info)" style={{ fontSize: 10, lineHeight: "16px" }}>
														{`{{${source.variable_name}}}`}
													</ContrastTag>
												</div>
												<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 3 }}>
													{source.description.substring(0, 50)}…
												</Text>
											</div>
										</Checkbox>
									</Card>
								</Col>
							))}
						</Row>
					</Checkbox.Group>
				</div>
			</div>

			{/* Step Editor Drawer */}
			<StepEditorDrawer
				visible={stepFormVisible}
				step={editingStepIndex !== null ? templateData.steps?.[editingStepIndex] : undefined}
				existingTopics={templateData.steps?.map((s) => s.title) || []}
				allSteps={templateData.steps || []}
				selectedDataSources={selectedDataSources}
				availableDataSources={availableDataSources}
				onSubmit={handleStepFormSubmit}
				onClose={() => setStepFormVisible(false)}
			/>
		</DetailDrawer>
	);
};

export default WorkflowBuilderDrawer;
